import React, { useState, useEffect } from 'react';
import { Trash2, ImageIcon, Loader2, X } from 'lucide-react';
import { apiService } from '../api/apiclient';
import { getThumbnailUrl, getVideoThumbnailUrl } from '../utils/getThumbnail';

const ImageGalleryPage = () => {
    const [images, setImages] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);

    // ✨ 全新加入：管理大图/视频预览的弹窗状态
    const [previewMedia, setPreviewMedia] = useState(null); // 结构: { url: string, isVideo: boolean } | null

    const fetchImages = async (pageNum = 1) => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await apiService.getFilesForUser(pageNum);
            if (response.success) {
                if (pageNum === 1) {
                    setImages(response.data);
                } else {
                    setImages(prev => [...prev, ...response.data]);
                }
                setHasMore(response.pagination.has_more);
            }
        } catch (error) {
            console.error("Image loaded failed:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchImages(1);
    }, []);

    const handleDeleteImage = async (url) => {
        if (!window.confirm("Are you sure you want to delete this file from the records?")) return;

        try {
            // 💡 健壮性优化：先用 new URL().pathname 抹除所有的 ?query 参数，再切出干净的文件名 Key
            const cleanPathname = new URL(url).pathname;
            const key = cleanPathname.split('/').pop();

            const res = await apiService.deleteFile(key);
            if (res.success) {
                setImages(images.filter(img => img !== url));
                // 如果当前正在预览的文件被删了，顺手关闭弹窗
                if (previewMedia?.url === url) {
                    setPreviewMedia(null);
                }
            }
        } catch (error) {
            alert("Failed to delete the image");
        }
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchImages(nextPage);
    };

    return (
        <div className="flex-1 p-8 bg-gray-50/50 min-h-full">
            <header className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-extrabold text-gray-950 tracking-tight">Gallery</h1>
            </header>

            {images.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center h-96 bg-white rounded-2xl border-2 border-dashed border-gray-200 text-gray-400 shadow-sm">
                    <ImageIcon size={48} className="mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No media in your gallery yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {images.map((file, index) => {
                        const isVideo = /\.(mp4|webm|ogg)$/i.test(file);
                        return (
                            <div
                                key={index}
                                // 💡 核心优化：点击原地弹出 Modal 预览，完美绕过浏览器的强制下载 Bug
                                onClick={() => setPreviewMedia({ url: file, isVideo })}
                                className="block relative w-full h-64 overflow-hidden group cursor-pointer rounded-2xl bg-white shadow-sm border border-gray-100"
                            >
                                {isVideo ? (
                                    <>
                                        <img
                                            src={getVideoThumbnailUrl(file)}
                                            alt="Video thumbnail"
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                        {/* 播放小图标叠加层 */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/15 group-hover:bg-black/25 transition-colors duration-200">
                                            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/95 shadow-md backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                                                <svg
                                                    className="w-5 h-5 text-gray-950 ml-0.5"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <img
                                        src={getThumbnailUrl(file)}
                                        alt="Gallery item"
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                )}

                                {/* 删除按钮 - 加上 stopPropagation 阻断弹窗触发 */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage(file);
                                    }}
                                    className="absolute top-3 right-3 p-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out shadow-md transform hover:scale-110 z-10"
                                    title="Delete"
                                >
                                    <Trash2 size={16} />
                                </button>

                                {/* 底部浮现的文件名阴影蒙版 */}
                                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 truncate font-mono">
                                    {file.split('/').pop()?.split('?')[0] || file}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 加载更多操作区 */}
            {hasMore && (
                <div className="mt-12 flex justify-center">
                    <button
                        onClick={handleLoadMore}
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-200 rounded-full font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 shadow-sm transition-all"
                    >
                        {loading ? <Loader2 className="animate-spin text-gray-500" size={18} /> : 'Load more'}
                    </button>
                </div>
            )}

            {/* ==================== ✨ 全新高级媒体预览模态框 (Modal) ==================== */}
            {previewMedia && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 md:p-8 select-none"
                    onClick={() => setPreviewMedia(null)} // 点击暗色背景关闭弹窗
                >
                    {/* 右上角悬浮关闭按钮 */}
                    <button
                        onClick={() => setPreviewMedia(null)}
                        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors shadow-lg border border-white/10"
                    >
                        <X size={22} />
                    </button>

                    {/* 核心内容容器 - 阻止冒泡避免点击图片本身关闭 */}
                    <div
                        className="relative max-w-5xl max-h-[85vh] w-full flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {previewMedia.isVideo ? (
                            <video
                                src={previewMedia.url}
                                controls
                                autoPlay
                                className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain bg-black"
                            />
                        ) : (
                            <img
                                src={previewMedia.url} // 大图直接拉取云端原图
                                alt="Media Large Preview"
                                className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageGalleryPage;